import { zodResolver } from '@hookform/resolvers/zod'
import { ReportTemplateType } from '@repo/shared/dtos/report'
import { reportTemplateValidationSchema } from '@repo/shared/validations/report-template-validation'
import { Button } from '@repo/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@repo/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { Textarea } from '@repo/ui/textarea'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { isEmpty } from 'lodash'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useStoreDispatch } from '@/store/hooks'
import {
  createReportTemplate,
  deleteReportTemplate,
  updateReportTemplate,
} from '@/store/report-templates/thunks'
import { ReportTemplate } from '@/store/report-templates/types'

export function ReportTemplateCreator({
  isDisabled,
  onCreated,
}: {
  isDisabled: boolean
  onCreated: (id: string) => void
}) {
  const { t } = useT({ keyPrefix: 'reports.creator' })
  const [open, setOpen] = useState(false)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger disabled={isDisabled} asChild>
        <Button variant="default" disabled={isDisabled}>
          <PlusIcon className="size-4" />
          {t('cta')}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-h-[90vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
        </DialogHeader>
        <ReportTemplateForm onSubmitted={() => setOpen(false)} onCreated={onCreated} />
      </DialogContent>
    </Dialog>
  )
}

export function ReportTemplateForm({
  editableReportTemplate,
  onCreated,
  onSubmitted,
}: {
  editableReportTemplate?: ReportTemplate
  onCreated?: (id: string) => void
  onSubmitted: () => void
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()
  const [isSaving, setIsSaving] = useState(false)
  const defaultValues = useMemo(
    () => ({
      name: editableReportTemplate?.name || '',
      description: editableReportTemplate?.description || '',
      instructions: editableReportTemplate?.instructions || '',
      type: editableReportTemplate?.type || ReportTemplateType.BENEFICIARY_REPORT,
    }),
    [editableReportTemplate],
  )

  const formApi = useForm({
    defaultValues,
    mode: 'onSubmit',
    resolver: zodResolver(reportTemplateValidationSchema),
    disabled: isSaving,
  })

  const { isDirty, errors, isValid } = formApi.formState

  const isFormDisabled = isSaving || !isDirty || !isEmpty(errors) || !isValid

  const submit = useCallback(async () => {
    if (isFormDisabled) return
    setIsSaving(true)

    const fields = formApi.getValues()

    if (editableReportTemplate) {
      // Update
      await dispatch(
        updateReportTemplate({
          reportTemplateId: editableReportTemplate.id,
          fields,
        }),
      )
    } else {
      // Create
      const res = await dispatch(createReportTemplate({ fields })).unwrap()
      onCreated?.(res.reportTemplate.id)
    }
    setIsSaving(false)
    onSubmitted()
  }, [isFormDisabled])

  const handleDelete = useCallback(async () => {
    if (!editableReportTemplate) return
    setIsSaving(true)

    await dispatch(deleteReportTemplate({ reportTemplate: editableReportTemplate }))

    setIsSaving(false)
    onSubmitted()
  }, [isFormDisabled])

  return (
    <Form {...formApi}>
      <form onSubmit={(e) => void formApi.handleSubmit(submit)(e)} className="space-y-4">
        <FormField
          control={formApi.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('name', { cfl: true })}</FormLabel>
              <FormControl>
                <Input
                  id="name"
                  placeholder={tc('name', { cfl: true })}
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={formApi.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {tc('description', { cfl: true })} ({tc('optional')})
              </FormLabel>
              <FormControl>
                <Textarea
                  id="description"
                  placeholder={tc('description', { cfl: true })}
                  className="resize-none"
                  rows={2}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={formApi.control}
          name="instructions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{tc('instructions', { cfl: true })}</FormLabel>
              <FormControl>
                <Textarea
                  id="instructions"
                  placeholder={tc('instructions', { cfl: true })}
                  className="resize-none"
                  rows={20}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-1 justify-between">
          {editableReportTemplate && (
            <TooltipWrapper content={tc('delete', { cfl: true })}>
              <Button onClick={() => void handleDelete()} isSensitive variant="outline" size="icon">
                <Trash2Icon className="size-4" />
              </Button>
            </TooltipWrapper>
          )}

          <Button type="submit" disabled={isFormDisabled}>
            {tc(editableReportTemplate ? 'update' : 'create', { cfl: true })}
          </Button>
        </div>
      </form>
    </Form>
  )
}
